import { useEffect, useState } from 'react';

// "Add to home screen" for the consumer vault (#252 D9). Chrome on Android
// offers its own install prompt through `beforeinstallprompt`, which fires
// once, early, often before the vault is on screen: this module catches it
// at app start (imported from main.tsx) and hands it out later. iPhones have
// no prompt; the vault shows the two taps instead.

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach(l => l());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', e => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
}

const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true);

export type InstallPlatform = 'ios' | 'android' | 'other';

const platform = (): InstallPlatform => {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'other';
};

export function useInstallPrompt() {
  const [, rerender] = useState(0);
  useEffect(() => {
    const l = () => rerender(n => n + 1);
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);

  const installed = isStandalone();
  const os = platform();
  return {
    installed,
    platform: os,
    // Phones only: on a laptop the vault isn't something to install.
    available: !installed && (!!deferred || os === 'ios' || os === 'android'),
    canPrompt: !!deferred,
    // Shows the browser's own prompt; false when it can't (show instructions instead).
    prompt: async (): Promise<boolean> => {
      if (!deferred) return false;
      const event = deferred;
      deferred = null;
      await event.prompt();
      const { outcome } = await event.userChoice;
      notify();
      return outcome === 'accepted';
    },
  };
}
